import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import { fetchRegions } from '../lib/catalog-client';
import { track } from '../lib/funnel';
import { useVisualViewportSheetHeight } from '../lib/use-visual-viewport-sheet-height';
import { Dropdown } from './dropdown';
import { Select } from './ui/select';
import { BudgetInput, BUDGET_CEILING_PICKS, BUDGET_FLOOR_PICKS } from './ui/budget-input';
import { Sheet, SheetContent, SheetClose, SheetTitle, SheetDescription } from './ui/sheet';
import { PinIcon, ClockIcon, SlidersIcon } from './icons';

const SORT_DEFAULT = 'newest';
const RANGE_KEYS = ['minBudget', 'maxBudget', 'deadlineFrom', 'deadlineTo'] as const;

/**
 * Unified catalog controls — Region, Status, Sort, Budget and Deadline in ONE
 * component. (Category + save-search stay prominent in {@link FilterBar} —
 * they're the activation hook, not a refinement filter. The Pro Excel export
 * moved out to the results-count line beside the density toggle — see
 * catalog.tsx.)
 *
 * Responsive: at md+ it's a single inline bar of dropdowns + range inputs. On
 * mobile it's results-first — a compact trigger row `[Фильтры (N)] [Сортировка]`;
 * the filters live in a bottom sheet so the tender list sits right under the
 * header. Sort is a separate quick trigger (frequent). All state is URL search
 * params.
 *
 * `scope`/`statusFixedByRoute`/`awarded` are still accepted (the caller passes
 * the route's resolved values) but only `scope` and `statusFixedByRoute` are
 * read here now; the others fed the export payload that moved to catalog.tsx.
 */
export function CatalogFilters({
  scope,
  statusFixedByRoute = false,
  customerName,
}: {
  scope?: string;
  /**
   * True ONLY where the ROUTE imposes the status and the `?status=` param is
   * ignored — today that is `/results` (the awarded archive). See the note at
   * `statusFixedByRoute` below for why the control is dropped there.
   *
   * ⚠️ This is a fact about the ROUTE, never about the URL. It used to be a
   * `status?: string` prop that catalog.tsx filled with its `effectiveStatus`,
   * and the component inferred "fixed by route" from `status != null`. But on
   * the ordinary catalog `effectiveStatus` IS the user's own pick, so the
   * moment anyone chose «Скоро закрытие» the prop went non-null and the Status
   * control deleted itself — on desktop and in the mobile sheet alike, with no
   * way back except «Сбросить фильтры», which also drops the region. Verified
   * on production 2026-09-21 at `/?region=AND&status=closing`. Pass a boolean
   * the caller has to state on purpose, so a value can never masquerade as a
   * constraint.
   */
  statusFixedByRoute?: boolean;
  awarded?: boolean;
  /** Display name for the pinned buyer (заказчик), `?customer=<tin>` — the
   *  URL only carries the TIN, so the caller resolves the name (from the
   *  current list's rows, since the TIN alone can't be looked up here) and
   *  hands it down. Undefined/null while unresolved: the chip falls back to
   *  the raw TIN rather than showing nothing. */
  customerName?: string | null;
} = {}) {
  const { t, locale } = useLocale();
  const [params, setParams] = useShellSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);
  /*
   * The mobile sheet follows the keyboard, like every payment sheet does.
   *
   * Measured 29.09 on a Redmi 15C inside Instagram's WebView: the keyboard came
   * up over «Не показывать тендеры со словами», the sheet stayed where it was,
   * and the reader typed blind. That WebView resizes NOTHING for a keyboard, so
   * no CSS unit can see it; the hook assumes (or, with the VirtualKeyboard API,
   * measures) one and publishes `--sheet-lift` / `--sheet-max`, which the panel
   * below consumes. Chrome on the same phone resizes the layout viewport itself
   * and was already fine; there the hook writes no lift at all.
   *
   * Gated on `sheetOpen`: the desktop bar never has a sheet, and a constant
   * `true` would pin the properties on the root for the whole catalog.
   */
  const sheetRef = useRef<HTMLDivElement>(null);
  useVisualViewportSheetHeight(sheetOpen, sheetRef);

  const { data: regions } = useQuery({
    queryKey: ['catalog', 'regions', locale],
    queryFn: () => fetchRegions(locale),
  });

  const region = params.get('region') ?? '';
  /** Buyer (заказчик) subscription axis — `?customer=<tin>`, reachable from a
   *  digest deep link (Task 5) as well as a future in-catalog buyer pick. Not
   *  a Dropdown/Select like the fields below (buyers aren't a closed set) —
   *  rendered as its own removable chip further down. */
  const customer = params.get('customer') ?? '';
  const status = params.get('status') ?? '';
  /*
   * ⚠️ On `/results` the status is imposed by the ROUTE, not chosen: catalog.tsx
   * computes `effectiveStatus = isAwarded ? 'closed' : status`, so the URL param
   * is ignored entirely there.
   *
   * The control was still rendered, reading `params.get('status')` — empty on a
   * cold `/results` load — so it displayed «Активные» over a page listing
   * finished, awarded lots. Relabelling it would have been worse: the dropdown
   * is INERT on that route, so a user who corrected it to «Закрытые» would see
   * nothing change and conclude the filters are broken.
   *
   * A choice that isn't one should not be offered. Where the route fixes the
   * status, the control is dropped; the page's own H1 and subtitle already say
   * these are completed procurements.
   *
   * That rule is about the ROUTE and nothing else — `statusFixedByRoute` now
   * arrives as an explicit prop rather than being inferred from "a status is
   * set", which made the control vanish on the user's own pick.
   */
  const sort = params.get('sort') ?? SORT_DEFAULT;
  const minBudget = params.get('minBudget') ?? '';
  const maxBudget = params.get('maxBudget') ?? '';
  const deadlineFrom = params.get('deadlineFrom') ?? '';
  const deadlineTo = params.get('deadlineTo') ?? '';
  /*
   * Минус-слова каталога.
   *
   * Появились после жалобы «ввёл минус-слово, а тендер остался в поиске»:
   * исключения существовали только в сохранённом поиске и действовали только
   * на оповещения, а список за диалогом их не знал вовсе. Фильтр, который
   * нельзя увидеть в работе, читается как сломанный.
   */
  const exclude = params.get('exclude') ?? '';

  // Local budget state so typing doesn't fire a query per keystroke.
  const [minLocal, setMinLocal] = useState(minBudget);
  const [maxLocal, setMaxLocal] = useState(maxBudget);
  const [excludeLocal, setExcludeLocal] = useState(exclude);
  useEffect(() => setMinLocal(minBudget), [minBudget]);
  useEffect(() => setMaxLocal(maxBudget), [maxBudget]);
  useEffect(() => setExcludeLocal(exclude), [exclude]);

  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // «до» below «от» is never what the reader meant, and the API answers it
    // with an honest empty list and no hint as to why. Swap the pair instead —
    // the inputs re-sync from the URL, so both fields show the corrected order.
    if (key === 'minBudget' || key === 'maxBudget') {
      const lo = next.get('minBudget');
      const hi = next.get('maxBudget');
      if (lo && hi && Number(lo) > Number(hi)) {
        next.set('minBudget', hi);
        next.set('maxBudget', lo);
      }
    }
    next.delete('page'); // any filter change resets pagination
    setParams(next);
    // Engagement: which refinements users actually apply. Only on set (not clear),
    // so it reads as "used the {key} filter", not "toggled it off".
    if (value) track('filter_applied', { filter: key });
  }

  function resetFilters() {
    const next = new URLSearchParams(params);
    // `kind` was missing here, so «Сбросить фильтры» cleared everything the user
    // could see EXCEPT the Товары/Работы/Услуги tab — which stayed selected and
    // kept cutting the result set. A reset that leaves a filter applied is worse
    // than no reset: the count does not return to what the user expects and
    // nothing on screen explains why.
    for (const k of [
      'region',
      'customer',
      'status',
      'kind',
      'sort',
      'exclude',
      ...RANGE_KEYS,
      'page',
    ])
      next.delete(k);
    setParams(next);
  }

  // Same rule as `resetFilters`'s own key list, for the one filter that has no
  // dropdown to clear itself: dropping just `customer` (+ pagination) rather
  // than every param.
  function clearCustomer() {
    const next = new URLSearchParams(params);
    next.delete('customer');
    next.delete('page');
    setParams(next);
  }

  // Count of applied refinements (region/status/ranges) — surfaced on the mobile
  // trigger so filters behind the sheet are never silently hidden. Sort +
  // category are excluded (their own controls / lines).
  const activeCount =
    (region ? 1 : 0) +
    (customer ? 1 : 0) +
    // Where the route fixes the status, a leftover `?status=` in the URL is not
    // applied to anything and has no control to clear it — counting it would
    // put a number on the mobile «Фильтры» badge that the sheet cannot explain.
    (!statusFixedByRoute && status ? 1 : 0) +
    (minBudget ? 1 : 0) +
    (maxBudget ? 1 : 0) +
    (deadlineFrom ? 1 : 0) +
    (deadlineTo ? 1 : 0) +
    (exclude ? 1 : 0);
  const hasAny = activeCount > 0;

  const allLabel = t('filter.all');
  const regionOptions = [
    { value: '', label: allLabel },
    ...(regions ?? []).map((r) => ({ value: r.slug, label: r.name })),
  ];
  const statusOptions = [
    { value: '', label: t('filter.status.active') },
    { value: 'closing', label: t('filter.status.closing') },
    { value: 'closed', label: t('filter.status.closed') },
    { value: 'all', label: t('filter.status.all') },
  ];
  const sortOptions = [
    { value: 'newest', label: t('catalog.sort.newest') },
    { value: 'deadline', label: t('catalog.sort.deadline') },
    { value: '-budgetUzs', label: t('catalog.sort.budgetDesc') },
    { value: 'budgetUzs', label: t('catalog.sort.budgetAsc') },
  ];

  const inputClass =
    'h-11 w-full rounded-md border border-edge bg-bone-surface px-3 text-[16px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_rgba(10,92,92,0.1)] md:h-8 md:px-2.5 md:text-[12.5px]';
  // Label ↔ field ids for the budget inputs. The desktop row and the mobile
  // sheet can both be in the DOM at once, hence the per-surface infix below.
  const idBase = useId();
  const dateClass =
    'min-w-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60';

  // Supply requests (cooperation.uz) and IFI notices carry no som budget, so a
  // budget range there filters on a field that is always null — every value
  // empties the list (audit 2026-09-07). Hide the inputs on those scopes.
  const showBudget = scope !== 'supply' && scope !== 'ifi';

  // Shared budget + deadline inputs (desktop row + mobile sheet). A function of
  // `inSheet`: on the phone each budget field takes the full sheet width and
  // carries quick picks and its «= 100 mln so'm» line (Igor's Redmi, 17.10);
  // the desktop row stays one line high, so there the field is only grouped.
  const rangeInputs = (inSheet: boolean) => (
    <>
      {showBudget && (
        <div className={`flex min-w-0 flex-col gap-1 ${inSheet ? 'col-span-2' : ''}`}>
          <label
            htmlFor={`${idBase}-${inSheet ? 's' : 'd'}-min-budget`}
            className="text-[11px] font-medium text-ink-muted"
          >
            {t('catalog.budgetFrom')}
          </label>
          <BudgetInput
            id={`${idBase}-${inSheet ? 's' : 'd'}-min-budget`}
            value={minLocal}
            onChange={setMinLocal}
            onBlur={() => update('minBudget', minLocal)}
            onKeyDown={(e) => e.key === 'Enter' && update('minBudget', minLocal)}
            // A pick applies at once: the field otherwise applies on blur,
            // and tapping a chip is not a blur of anything the reader typed.
            onPick={(d) => update('minBudget', d)}
            presets={inSheet ? BUDGET_FLOOR_PICKS : undefined}
            showCaption={inSheet}
            className={inputClass}
          />
        </div>
      )}
      {showBudget && (
        <div className={`flex min-w-0 flex-col gap-1 ${inSheet ? 'col-span-2' : ''}`}>
          <label
            htmlFor={`${idBase}-${inSheet ? 's' : 'd'}-max-budget`}
            className="text-[11px] font-medium text-ink-muted"
          >
            {t('catalog.budgetTo')}
          </label>
          <BudgetInput
            id={`${idBase}-${inSheet ? 's' : 'd'}-max-budget`}
            value={maxLocal}
            onChange={setMaxLocal}
            onBlur={() => update('maxBudget', maxLocal)}
            onKeyDown={(e) => e.key === 'Enter' && update('maxBudget', maxLocal)}
            onPick={(d) => update('maxBudget', d)}
            presets={inSheet ? BUDGET_CEILING_PICKS : undefined}
            showCaption={inSheet}
            className={inputClass}
          />
        </div>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-ink-muted">{t('catalog.deadlineFrom')}</span>
        <input
          type="date"
          value={deadlineFrom}
          onChange={(e) => update('deadlineFrom', e.target.value)}
          className={`${inputClass} ${dateClass}`}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-ink-muted">{t('catalog.deadlineTo')}</span>
        <input
          type="date"
          value={deadlineTo}
          onChange={(e) => update('deadlineTo', e.target.value)}
          className={`${inputClass} ${dateClass}`}
        />
      </label>
    </>
  );

  // Минус-слова — own field so it can sit inline in the desktop filter row
  // (between Status and Sort) and span full width in the mobile sheet, rather
  // than orphaning onto a third grid line. Свободный текст, поэтому
  // применяется по blur/Enter, как бюджет, а не на каждый символ.
  //
  // The caption says what the field DOES («Не показывать тендеры со словами»)
  // and the placeholder carries no example words (2026-09-30). Under «Исключить
  // слова» with «например: аренда, благоустройство», 81 of 83 users who filled
  // it typed what they were LOOKING for — and got every tender except their
  // product. Example words read as a search example and got copied.
  const excludeInput = (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-ink-muted">{t('catalog.excludeWords')}</span>
      <input
        type="text"
        value={excludeLocal}
        placeholder={t('catalog.excludeWordsPlaceholder')}
        onChange={(e) => setExcludeLocal(e.target.value)}
        onBlur={() => update('exclude', excludeLocal.trim())}
        onKeyDown={(e) => e.key === 'Enter' && update('exclude', excludeLocal.trim())}
        className={inputClass}
      />
    </label>
  );

  return (
    <div className="mt-3">
      {/* Buyer (заказчик) subscription axis — the one active filter that isn't a
          Dropdown/Select (buyers aren't a closed set), so it gets its own
          removable chip instead. Shown on both desktop and mobile, above the
          layout split below, so a digest-link visitor sees immediately WHICH
          buyer narrowed the list they landed on. */}
      {customer && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-md bg-brand-dim px-2 py-1 text-[12.5px] font-semibold text-brand">
            {t('filter.customer')}: {customerName ?? customer}
            <button
              type="button"
              aria-label={t('filter.chip.remove', { name: customerName ?? customer })}
              onClick={clearCustomer}
              className="text-[14px] leading-none text-brand/70 transition-colors hover:text-brand"
            >
              ×
            </button>
          </span>
        </div>
      )}

      {/* ── Desktop: a fixed 4-column grid, capped so it doesn't stretch edge-
          to-edge on wide screens. Row 1 = Region · Status · exclude words ·
          Sort; row 2 = the four range inputs. `items-end` keeps the exclude
          input's box bottom-aligned with the self-labelled dropdowns beside it
          (its caption floats above, and may wrap in a narrow column). Export moved to the
          actions line below so row 1 stays a clean four across. ──────────────── */}
      <div className="hidden md:block md:max-w-5xl">
        <div className="grid grid-cols-4 items-end gap-3">
          <Dropdown
            variant="field"
            fullWidth
            icon={<PinIcon className="h-full w-full" />}
            label={t('filter.region')}
            ariaLabel={t('filter.region')}
            active={region !== ''}
            value={region}
            onChange={(v) => update('region', v)}
            options={regionOptions}
          />
          {!statusFixedByRoute && (
            <Dropdown
              variant="field"
              fullWidth
              icon={<ClockIcon className="h-full w-full" />}
              label={t('filter.status')}
              ariaLabel={t('filter.status')}
              active={status !== ''}
              value={status}
              onChange={(v) => update('status', v)}
              options={statusOptions}
            />
          )}
          {excludeInput}
          <Dropdown
            variant="field"
            fullWidth
            label={t('catalog.sortLabel')}
            ariaLabel={t('catalog.sortLabel')}
            active={sort !== SORT_DEFAULT}
            value={sort}
            onChange={(v) => update('sort', v === SORT_DEFAULT ? '' : v)}
            options={sortOptions}
          />
        </div>
        {/* Budget + Deadline — same four columns, aligned under the filters. */}
        <div className="mt-3 grid grid-cols-4 gap-3 border-t border-edge/70 pt-3">
          {rangeInputs(false)}
        </div>
        {/* Clear — right-aligned under the grid. (Excel export moved to the
            results-count line, next to the density toggle — see catalog.tsx.) */}
        {hasAny && (
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-8 items-center rounded-md px-2 text-[12.5px] font-semibold text-ink-muted transition-colors hover:text-ink"
            >
              {t('filter.clear')}
            </button>
          </div>
        )}
      </div>

      {/* ── Mobile: equal-width trigger row (full width) → bottom sheet ─────── */}
      <div className="flex items-stretch gap-2 md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md border border-edge bg-bone-surface px-2 text-[13px] font-semibold text-ink transition-colors hover:border-brand/30"
        >
          <SlidersIcon className="h-4 w-4 text-ink-soft" />
          {t('catalog.filters')}
          {activeCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-brand px-1.5 text-[11px] font-bold text-white">
              {activeCount}
            </span>
          )}
        </button>

        {/* Sort — a separate quick trigger (frequent). Custom DS Select (never a
            native <select>: the OS picker breaks the m16 look). */}
        <Select
          value={sort}
          aria-label={t('catalog.sortLabel')}
          onChange={(v) => update('sort', v === SORT_DEFAULT ? '' : v)}
          options={sortOptions}
          className={`h-11 min-w-0 flex-1 justify-center text-[13px] font-semibold ${
            sort !== SORT_DEFAULT ? 'border-brand/40 bg-brand-dim text-brand' : ''
          }`}
        />
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          ref={sheetRef}
          side="bottom"
          /*
           * Lifted by `--sheet-lift` (its `margin-bottom` on a `fixed; bottom: 0`
           * box), capped by `--sheet-max` — and the panel is the sheet's ONE
           * scroller, the element the hook centres the focused field in. The
           * column inside used to scroll on its own: with the cap on, the field
           * would sit clipped in THAT box while the hook scrolled this one, and
           * `ui/sheet`'s swipe-to-dismiss, which arms when the PANEL's
           * `scrollTop` is 0, would be reading a panel that never scrolls. The
           * no-var fallback is `92vh` with a `supports-[height:1dvh]` twin: a
           * lone `dvh` is no cap at all on Chrome < 108 (see `SHEET_MAX_RATIO`).
           */
          className="mb-[var(--sheet-lift,0px)] max-h-[var(--sheet-max,92vh)] gap-4 overflow-y-auto overscroll-contain rounded-t-2xl border-t border-edge bg-bone-surface p-5 supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)]"
        >
          <SheetDescription className="sr-only">{t('catalog.filters.a11yDesc')}</SheetDescription>
          <div className="flex items-center justify-between">
            <SheetTitle className="font-brand text-[18px] font-extrabold tracking-[-0.3px] text-ink">
              {t('catalog.filters')}
            </SheetTitle>
            {hasAny && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-[13px] font-semibold text-ink-muted transition-colors hover:text-ink"
              >
                {t('filter.clear')}
              </button>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-ink-muted">{t('filter.region')}</span>
              <Select
                value={region}
                aria-label={t('filter.region')}
                onChange={(v) => update('region', v)}
                options={regionOptions}
              />
            </label>
            {!statusFixedByRoute && (
              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-medium text-ink-muted">{t('filter.status')}</span>
                <Select
                  value={status}
                  aria-label={t('filter.status')}
                  onChange={(v) => update('status', v)}
                  options={statusOptions}
                />
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">{rangeInputs(true)}</div>
            {/* Минус-слова — full width in the sheet, below the paired range
                inputs, so the free-text field has room for a phrase. */}
            {excludeInput}
          </div>

          <SheetClose asChild>
            <button
              type="button"
              className="mt-1 inline-flex h-12 w-full items-center justify-center rounded-md bg-brand text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
            >
              {t('catalog.filtersApply')}
            </button>
          </SheetClose>
        </SheetContent>
      </Sheet>
    </div>
  );
}
